import { InsufficientBalanceButton } from 'components/Buttons/InsufficientBalance'
import {
  LongOrderButton,
  ShortOrderButton,
} from 'components/Buttons/OrderButton'
import { UserActionGuideButton } from 'components/Buttons/UserActionGuide'
import toastify from 'components/Toastify'
import useIsEnoughBalance from 'hooks/useIsEnoughBalance'
import i18n from 'i18n'
import { memo } from 'react'
import { useAppDispatch } from 'store'
import {
  useCurrencyBalance,
  useIsActivated,
  useL2AccountId,
} from 'store/account/hooks'
import { useActionState } from 'store/app/hooks'
import { useMarketPrice } from 'store/market/hooks'
import { useOrderStopPrice } from 'store/order/hooks'
import { createSpotOrderAction } from 'store/spot/actions/createOrderActions'
import {
  useCreateOrderOption,
  useCurrentSpotSymbol,
  useSpotFeeRates,
  useSpotOrderDirection,
  useSpotSelectedOrderMode,
} from 'store/spot/hooks'
import { OrderDirection, OrderModeNames } from 'store/spot/types'
import { gaEvent, GaEventName } from 'utils/ga'
import { isZero, toFixed } from 'utils/number'
import { useTradingFeeRate } from '../../../../hooks/useTradingFees'
import { Ether } from 'types'
import { TradeType } from 'store/market/types'
import { checkOrderValue } from 'utils/checkOrderValue'
import { useOrderPriceByMode } from 'hooks/zkex/useOrderPriceByMode'
import { useSetAtom } from 'jotai'
import { orderPercentAtom } from '../AmountPercent/atoms'

export const ConnectOrOrderButton = memo(() => {
  const dispatch = useAppDispatch()
  const orderDirection = useSpotOrderDirection()
  const isBuy = orderDirection === OrderDirection.LONG
  const layer2Id = useL2AccountId()
  const { limitPrice, baseCurrencyAmount, quoteCurrencyAmount } =
    useCreateOrderOption()
  const {
    id = 0,
    name = '',
    baseCurrency = '',
    quoteCurrency = '',
    l2QuoteCurrencyId = 0,
    l2BaseCurrencyId = 0,
    baseScale = 0,
    quoteScale = 0,
    baseMinimumQuantity = 0,
  } = useCurrentSpotSymbol()
  const balanceBase = useCurrencyBalance(baseCurrency)
  const balanceQuote = useCurrencyBalance(quoteCurrency)
  const balance = isBuy ? balanceQuote : balanceBase
  const payAmount = isBuy ? quoteCurrencyAmount : baseCurrencyAmount
  const isEnoughBalance = useIsEnoughBalance(
    payAmount,
    balance?.available ?? '0',
  )
  const orderPending = useActionState('ordering')
  const spotSelectedOrderMode = useSpotSelectedOrderMode()
  const marketPrice = useMarketPrice(name)
  const isActivated = useIsActivated()
  const stopPrice = useOrderStopPrice()
  const feeRates = useSpotFeeRates()
  const setPercent = useSetAtom(orderPercentAtom)

  let price: Ether = useOrderPriceByMode(
    spotSelectedOrderMode.name,
    limitPrice,
    marketPrice[orderDirection],
    stopPrice,
    TradeType.Spot,
    orderDirection,
  )

  const tradingFeeRate = useTradingFeeRate(
    price,
    baseCurrencyAmount,
    feeRates?.maker!,
    feeRates?.taker!,
  )

  const [valueValid, valueMessage] = checkOrderValue(
    baseCurrencyAmount,
    price,
    baseMinimumQuantity,
  )

  const isStopOrder =
    spotSelectedOrderMode.name === OrderModeNames.StopLimit ||
    spotSelectedOrderMode.name === OrderModeNames.StopMarket

  const onSubmit = () => {
    if (isZero(baseCurrencyAmount)) {
      toastify.error('Enter a amount')
      return
    }

    if (!id) {
      toastify.error('Missing currentSpotSymbol')
      return
    }

    if (isZero(price)) {
      toastify.error(`Missing or invalid price filed ${price}.`)
      return
    }

    dispatch(
      createSpotOrderAction({
        orderMode: spotSelectedOrderMode,
        orderDirection: orderDirection,
        l2AccountId: layer2Id,
        symbol: name,
        price,
        quoteScale,
        stopPrice,
        amount: toFixed(baseCurrencyAmount, baseScale),
        feeRates: {
          maker: tradingFeeRate.maker,
          taker: tradingFeeRate.taker,
        },
        baseTokenId: l2BaseCurrencyId,
        quoteTokenId: l2QuoteCurrencyId,
      }),
    ).finally(() => {
      setPercent(0)
    })
  }
  const renderButton = () => {
    const OrderButton = isBuy ? LongOrderButton : ShortOrderButton
    if (!isActivated) {
      return <UserActionGuideButton />
    }

    if (isStopOrder) {
      if (isZero(stopPrice)) {
        return (
          <OrderButton disabled={true}>
            {i18n.t('create-order-place-limit-order')}
          </OrderButton>
        )
      }
    }
    if (
      isZero(limitPrice) &&
      spotSelectedOrderMode.name !== OrderModeNames.StopMarket
    ) {
      return (
        <OrderButton disabled={true}>
          {i18n.t('create-order-place-limit-order')}
        </OrderButton>
      )
    }

    if (!valueValid) {
      return <OrderButton disabled={true}>{valueMessage}</OrderButton>
    }

    if (isEnoughBalance === false) {
      // Don't show insufficient balance button for stop order
      if (!isStopOrder) return <InsufficientBalanceButton />
    }

    if (orderPending === true) {
      return (
        <OrderButton loading={true} disabled={true}>
          {i18n.t('create-order-placing')}
        </OrderButton>
      )
    }
    return (
      <OrderButton onClick={onSubmit}>
        {isBuy
          ? `${i18n.t('create-order-buy', { defaultValue: 'Buy' })}`
          : `${i18n.t('create-order-sell', { defaultValue: 'Sell' })}`}
      </OrderButton>
    )
  }
  return <>{renderButton()}</>
})
